import { useEffect, useRef, useCallback } from 'react';
import { websocketService } from '../../../shared/services/websocketService';
import { bannerService, ProdutoBanner } from '../../banners/services/bannerService';

interface UsePriceUpdateOptions {
  screenId: number | null;
  produtoIds: number[]; // IDs dos produtos que estão sendo exibidos na tela atual
  onPricesUpdated: (precosAtualizados: ProdutoBanner[]) => void;
  enabled?: boolean;
}

/**
 * Hook para receber atualizações de preços de produtos via SignalR
 * 
 * Quando recebe o evento "ProdutoAtualizado" do SignalR:
 * 1. Busca os preços atualizados dos produtos da tela atual via API
 * 2. Chama o callback onPricesUpdated com os preços atualizados
 * 
 * Funcionalidades:
 * - Atualizações em tempo real de preços
 * - Busca assíncrona apenas dos produtos da tela atual
 * - Funciona para todos os usuários simultaneamente
 */
export const usePriceUpdate = ({
  screenId,
  produtoIds,
  onPricesUpdated,
  enabled = true
}: UsePriceUpdateOptions) => {
  const isMountedRef = useRef<boolean>(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  // Refs para valores que mudam - evitar re-registrar callback
  const onPricesUpdatedRef = useRef(onPricesUpdated);
  const produtoIdsRef = useRef(produtoIds);
  
  // Atualizar refs quando valores mudarem
  useEffect(() => {
    onPricesUpdatedRef.current = onPricesUpdated;
    produtoIdsRef.current = produtoIds;
  }, [onPricesUpdated, produtoIds]);

  /**
   * Handler para atualização de preços
   * Segue EXATAMENTE o padrão da TelevisaoAtualizada:
   * 1. O SignalR já fez o delay (500ms) no handler
   * 2. Buscar preços atualizados via API (com retry se necessário)
   * 3. Chamar callback com os preços atualizados
   */
  const handlePriceUpdate = useCallback(async (produtoIdsParam: number[]) => {
    if (!isMountedRef.current) {
      return;
    }

    // IMPORTANTE: Usar os produtoIds da tela atual (do ref)
    // O SignalR não envia quais produtos foram atualizados, então buscamos TODOS os produtos da tela
    const idsParaBuscar = produtoIdsRef.current.length > 0 
      ? produtoIdsRef.current 
      : produtoIdsParam;

    if (idsParaBuscar.length === 0) {
      return;
    }

    try {
      // PASSO 1: Fazer GET dos preços (sem parâmetros adicionais como t ou nocache)
      // Seguir exatamente o padrão da televisão: delay já foi feito no handler do SignalR
      let precosAtualizados: ProdutoBanner[] | undefined;
      let tentativas = 0;
      const maxTentativas = 3;
      
      while (tentativas < maxTentativas) {
        try {
          // Fazer GET dos preços (mesmo padrão da televisão)
          precosAtualizados = await bannerService.buscarPrecosAtualizados(idsParaBuscar);
          
          // IMPORTANTE: A API pode retornar array vazio [] se não encontrar produtos
          // Mas se retornou um array (mesmo vazio), a requisição foi bem-sucedida
          // Só continuar tentando se der erro ou se não for array
          if (Array.isArray(precosAtualizados)) {
            break; // Sucesso - mesmo que array vazio, a requisição funcionou
          }
        } catch (error) {
          console.warn(`[PriceUpdate] ❌ Erro na tentativa ${tentativas + 1}:`, error);
        }
        
        tentativas++;
        if (tentativas < maxTentativas) {
          // Aguardar mais um pouco antes de tentar novamente
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      if (!isMountedRef.current) {
        return;
      }

      // IMPORTANTE: Se a API retornou array vazio, não é erro - apenas não há preços para atualizar
      if (!precosAtualizados || !Array.isArray(precosAtualizados)) {
        return;
      }

      if (precosAtualizados.length === 0) {
        return;
      }

      // PASSO 2: Chamar callback com preços atualizados
      onPricesUpdatedRef.current(precosAtualizados);
    } catch (error) {
      console.error(`[PriceUpdate] ❌ Erro ao processar atualização de preços:`, error);
    }
  }, [screenId]);

  // Configurar monitoramento quando o componente montar ou quando screenId/produtoIds mudarem
  useEffect(() => {
    if (!enabled || !screenId) {
      return;
    }

    isMountedRef.current = true;

    // Conectar e registrar callback
    const setup = async () => {
      try {
        // Conectar ao WebSocket se não estiver conectado
        if (!websocketService.isConnected()) {
          await websocketService.connect({
            autoReconnect: true,
            reconnectInterval: 3000,
            maxReconnectAttempts: 10
          });
        }

        // Remover callback anterior se existir
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        
        const unsubscribe = websocketService.onPriceUpdate(screenId, handlePriceUpdate);
        unsubscribeRef.current = unsubscribe;
      } catch (error) {
        console.error('[PriceUpdate] ❌ Erro ao configurar WebSocket:', error);
      }
    };

    setup();

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [screenId, enabled, handlePriceUpdate]);
};
